import { AspectRatio, Flex, Button, Modal } from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { generateGetSignedUrl } from 'api/action';
import { useResendFax } from 'api/action/fax';
import { useFetchFaxesBySite } from 'api/resource/fax';
import { FaxColumns } from 'columns/Fax';
import { BillingDataGridWrapper } from 'components/common';
import { Fax } from 'interfaces/fax';
import { MRT_TableOptions } from 'mantine-react-table';
import { useState } from 'react';
import { useTranslation } from '@refinedev/core';
import { parseFaxAttachmentS3Uri } from 'utils/faxUtils';
import { useCurrentSiteStore } from 'store/SiteStore';

export const FaxManagement: React.FC = () => {
  const { translate } = useTranslation();

  const title = translate('pages:faxes.pageTitle');
  useDocumentTitle(translate('pages:faxes.documentTitle'));

  const { id: siteID } = useCurrentSiteStore();
  const { data: faxes, isLoading, isError } = useFetchFaxesBySite({ siteID });

  const resendFax = useResendFax();

  const [openAttachment, setOpenAttachment] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');

  const handleResendFax = async (faxID: string) => {
    try {
      await resendFax.mutateAsync({
        faxID,
      });
    } catch (error) {
      console.error('Error resending fax:', error);
    }
  };

  const handleViewAttachment = async (attachmentKey: string) => {
    try {
      const { bucket, key } = parseFaxAttachmentS3Uri(attachmentKey);

      const signed = await generateGetSignedUrl({
        bucket,
        key,
      });

      setOpenAttachment(true);
      setUrl(signed);
    } catch (error) {
      console.error('Error generating signed URL for fax attachment:', error);
    }
  };

  const columns = FaxColumns({ handleResendFax, handleViewAttachment });

  const tableOptions: MRT_TableOptions<Fax> = {
    data: faxes || [],
    columns,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    initialState: {
      sorting: [
        {
          id: 'createdAt',
          desc: true,
        },
      ],
    },
  };

  return (
    <>
      {openAttachment && url && (
        <Modal
          opened={openAttachment}
          onClose={() => setOpenAttachment(false)}
          title="Fax Attachment"
          size={'lg'}
        >
          <AspectRatio ratio={4 / 3} style={{ width: '100%' }}>
            <iframe
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
              style={{ border: 'none', flex: 1 }}
              title="Fax Attachment"
            />
          </AspectRatio>
          <Flex justify="flex-end" mt="sm">
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
            >
              {translate('common:downloadPDF')}
            </Button>
          </Flex>
        </Modal>
      )}
      <BillingDataGridWrapper
        title={title}
        tableOptions={tableOptions}
        dataTestID="faxManagement"
        showAlertBanner={false}
      />
    </>
  );
};
